/* eslint-disable no-unused-vars */
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import xlsx from 'json-as-xlsx'
import { getSiteEvents } from '../model'

/**
 * Column format to convert object property `dataKey` to Excel column `title`
 *
 * `title` Excel/Pdf Column title gets converted from `dataKey`
 *
 * `dataKey` the object propert(ies) used
 */
const columns = [
  { title: 'Date', dataKey: 'creationDate' },
  { title: 'Time', dataKey: 'timeOfRecord' },
  { title: 'Water Level', dataKey: 'waterLevel' },
  { title: 'flowObservation', dataKey: 'flowObservation' },
  { title: 'Flow', dataKey: 'flowRate' },
  { title: 'waterlevelObservation', dataKey: 'waterlevelObservation' },
  { title: 'rainfallObservation', dataKey: 'rainfallObservation' },
  { title: 'windObservation', dataKey: 'windObservation' },
  { title: 'skyObservation', dataKey: 'skyObservation' },
  { title: 'AirT', dataKey: 'airTemp' },

  { title: 'DepthSample', dataKey: 'sampleDepth' },
  { title: 'Water Temp', dataKey: 'waterTemp' },
  { title: 'EC', dataKey: 'EC' },
  { title: 'pH', dataKey: 'pH' },
  { title: 'Turbidity', dataKey: 'turbidity' },
  { title: 'waterNotes', dataKey: 'waterNotes' },
  { title: 'pastRainfall', dataKey: 'pastRainfall' },
  { title: 'labSample', dataKey: 'labSample' },
  { title: 'aBottle', dataKey: 'aBottle' },
  { title: 'dBottle', dataKey: 'dBottle' },
  { title: 'floodComments', dataKey: 'floodComments' },
  { title: 'Notes', dataKey: 'siteNotes' },
  { title: 'Samplers', dataKey: 'samplers' },

  { title: 'Notes', dataKey: 'notes' },

  { title: 'signalIndex', dataKey: 'signalIndex' },
  { title: 'pollutionIndex', dataKey: 'pollutionIndex' },
  { title: 'catchmentIndex', dataKey: 'catchmentIndex' },
]

const otherColumns = []
columns.forEach((column) => {
  otherColumns.push({ label: column.title, value: column.dataKey })
})

export const donwloadEventXLSX = async (siteId, year) => {
  const data = await getSiteEvents(siteId)
  console.log(`Excel columns: ${JSON.stringify(otherColumns)}`)
  console.log(`Excel data: ${JSON.stringify(data)}`)
  let excelData = [
    {
      sheet: 'Field_data',
      columns: otherColumns,
      content: data,
    },
  ]

  let settings = {
    fileName: `${siteId}`, // Name of the resulting spreadsheet
    extraLength: 3, // A bigger number means that columns will be wider
    writeMode: 'writeFile', // The available parameters are 'WriteFile' and 'write'. This setting is optional. Useful in such cases https://docs.sheetjs.com/docs/solutions/output#example-remote-file
    writeOptions: {}, // Style options from https://github.com/SheetJS/sheetjs#writing-options
  }

  xlsx(excelData, settings) // Will download the excel file
}

/**
 * @param siteId
 * @param year events in specific year, `null` will mean all
 */
export const downloadEventsPDF = async (siteId, year) => {
  const filename = `${siteId}.pdf`
  const data = await getSiteEvents(siteId)

  const doc = new jsPDF('landscape')

  doc.autoTable(columns, data, {
    styles: {
      fontSize: 4,
    },
  })

  doc.save(filename)
}

/**
 * @param siteId
 * @param year events in specific year, `null` will mean all events
 */
export const downloadEventsCSV = async (siteId, year) => {
  const filename = `${siteId}.csv`
  const data = await getSiteEvents(siteId)

  const isNodeEnvironment = typeof window === 'undefined'
  const href = isNodeEnvironment ? '' : buildURI(data, columns)

  // If this browser is IE 11, it does not support the `download` attribute
  const blob = new Blob([filename], { type: 'text/csv;charset=utf-8;' })
  if (window.navigator.msSaveBlob) {
    // IE 10+
    window.navigator.msSaveBlob(blob, filename)
  } else {
    let link = document.createElement('a')
    link.setAttribute('download', filename)
    link.setAttribute('href', href)
    document.body.appendChild(link) // Required for FF

    link.click()
    document.body.removeChild(link)
  }
}

//-------------------------------------BUILDING CSV-----------------------------------------------

function buildURI(data, headers, separator = ',', enclosingCharacter = '"') {
  const csv = toCSV(data, headers, separator, enclosingCharacter)
  const type = isSafari() ? 'application/csv' : 'text/csv'
  const blob = new Blob([csv], { type })
  const dataURI = `data:${type};charset=utf-8,${csv}`

  const URL = window.URL || window.webkitURL

  return typeof URL.createObjectURL === 'undefined'
    ? dataURI
    : URL.createObjectURL(blob)
}

/**
 * Simple safari detection based on user agent test
 */
const isSafari = () =>
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

const isJsons = (array) =>
  Array.isArray(array) &&
  array.every((row) => typeof row === 'object' && !(row instanceof Array))

/**
 * 
    headers = [
        { title: "SiteCode", key: "siteCode" },
        { title: "Site Name", key: "siteName" },
        { title: "Region", key: "region" }
    ];
*/
const jsonsHeaders = (array) =>
  Array.from(
    array
      .map((json) => Object.keys(json))
      .reduce((a, b) => new Set([...a, ...b]), [])
  )

/**
 * @param {Object[]} jsons -
 * @param {Object[]} headers - array of headers {Label to display at the top of the CSV, Key to the data}
 * @param {string} headers[].title - Title to display at the top of the CSV
 * @param {string} headers[].dataKey -  Key to the data
 */
const jsons2arrays = (jsons, headers) => {
  headers = headers || jsonsHeaders(jsons)

  // allow headers to have custom labels, defaulting to having the header data key be the label
  let headerTitles = headers
  let headerKeys = headers
  if (isJsons(headers)) {
    headerTitles = headers.map((header) => header.title)
    headerKeys = headers.map((header) => header.dataKey)
  }

  const data = jsons.map((object) =>
    headerKeys.map((header) => getHeaderValue(header, object))
  )
  return [headerTitles, ...data]
}

function getHeaderValue(property, obj) {
  const foundValue = property
    .replace(/\[([^\]]+)]/g, '.$1')
    .split('.')
    .reduce(function (o, p, i, arr) {
      // if at any point the nested keys passed do not exist, splice the array so it doesnt keep reducing
      const value = o[p]
      if (value === undefined || value === null) {
        arr.splice(1)
      } else {
        return value
      }
    }, obj)
  // if at any point the nested keys passed do not exist then looks for key `property` in object obj
  return foundValue === undefined
    ? property in obj
      ? obj[property]
      : ''
    : foundValue
}

function joiner(data, separator = ',', enclosingCharacter = '"') {
  const elementOrEmpty = (element) =>
    typeof element === 'undefined' || element === null ? '' : element

  return data
    .filter((e) => e)
    .map((row) =>
      row
        .map((element) => elementOrEmpty(element))
        .map((column) => `${enclosingCharacter}${column}${enclosingCharacter}`)
        .join(separator)
    )
    .join(`\n`)
}

function toCSV(data, headers, separator, enclosingCharacter) {
  if (isJsons(data)) {
    const jsons2csv = joiner(
      jsons2arrays(data, headers),
      separator,
      enclosingCharacter
    )
    return jsons2csv
  }
  throw new TypeError(`Data should be "Array of objects" `)
}

//-----------------------------------END - BUILDING CSV------------------------------------------------
